import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Stack,
  Typography,
  Box,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Grid,
  Link as MuiLink,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import toast from 'react-hot-toast';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import { useCapsLockDetector } from '../hooks/useCapsLockDetector';
import { useSendForgotPasswordEmailMutation } from '../authApiSlice';

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
});

const ForgotPasswordForm = ({ onSwitch }) => {
  const isCapsLockOn = useCapsLockDetector();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [sendForgotPasswordEmail, { isLoading, error }] = useSendForgotPasswordEmailMutation();

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const host = process.env.REACT_APP_HOSTED_URL;
      const promise = sendForgotPasswordEmail({
        email: values.email,
        host,
      }).unwrap();

      toast.promise(promise, {
        loading: 'Sending email...',
        success: 'Email sent successfully',
        error: (err) => {
          resetForm();
          if (err.data && err.data.message) {
            return err.data.message;
          }
          return 'Error sending email. Please try again.';
        },
      });
      await promise;
      resetForm();
    },
  });
  return (
    <Stack direction='column' justifyContent='center' spacing={isSmallScreen ? 1 : 2} width='100%'>
      <Box
        mb={isSmallScreen ? 1 : 2}
        flexDirection='column'
        display='flex'
        alignItems='center'
        sx={{ marginTop: 'auto' }}
      >
        <LockPersonOutlinedIcon
          sx={{
            color: 'text.secondary',
            height: '10vh',
            width: '10vw',
            opacity: '0.8',
          }}
        />
      </Box>
      <Typography align='center' variant='h5'>
        Forgot Password
      </Typography>
      <Typography align='center' color='text.secondary'>
        Enter your email to reset your password
      </Typography>
      <Box display='flex' flexDirection='column' textAlign='center' justifyContent='center'>
        <form onSubmit={formik.handleSubmit}>
          <Box mb={1}>
            <TextField
              size='normal'
              margin='dense'
              label='Email'
              variant='outlined'
              name='email'
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && !!formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <EmailIcon color='action' />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: isSmallScreen ? '0.8rem' : '0.9rem',
                },
              }}
            />
            {isCapsLockOn && (
              <Typography variant='body2' color='gray'>
                Caps Lock is on
              </Typography>
            )}
          </Box>
          <Box textAlign='center'>
            <MuiLink
              component='span'
              onClick={onSwitch}
              underline='none'
              sx={{
                color: 'grey',
                cursor: 'default',
                '&:hover': {
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                },
              }}
            >
              Back to Login
            </MuiLink>
          </Box>
          <Box>
            <Button type='submit' variant='contained' size='medium' sx={{ marginTop: isSmallScreen ? 1 : 2 }}>
              Submit
            </Button>
          </Box>
        </form>
      </Box>
    </Stack>
  );
};

export default ForgotPasswordForm;
